import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from './ui/Button';
import Icon from './AppIcon';
import { initializeTrackingScripts, updateTrackingScripts } from '../utils/cookieConsent';

// Exportiere Funktion zum Öffnen der Cookie-Einstellungen (für Footer-Link)
export const openCookieSettings = () => {
  const event = new CustomEvent('openCookieSettings');
  window.dispatchEvent(event);
};

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Wenn bereits Consent vorhanden, Scripts initialisieren
      initializeTrackingScripts();
    }

    // Event Listener für externes Öffnen der Einstellungen
    const handleOpenSettings = () => {
      setShowSettings(true);
    };
    window.addEventListener('openCookieSettings', handleOpenSettings);
    
    return () => {
      window.removeEventListener('openCookieSettings', handleOpenSettings);
    };
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    setShowBanner(false);
    
    // Aktiviere Tracking-Scripts
    initializeTrackingScripts();
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    setShowBanner(false);
    
    // Aktualisiere Tracking-Scripts (deaktiviere alle)
    updateTrackingScripts(consent);
  };

  const handleSaveSettings = (settings) => {
    const consent = {
      ...settings,
      necessary: true, // Immer erforderlich
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
    
    // Aktualisiere Tracking-Scripts basierend auf neuen Einstellungen
    updateTrackingScripts(consent);
  };

  // Zeige auch Settings-Modal, wenn es extern geöffnet wurde
  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 lg:p-6" style={{ zIndex: 9998, pointerEvents: 'auto' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-start space-x-3 mb-3">
                <Icon name="Cookie" size={24} className="text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-text-primary mb-2">
                    Wir verwenden Cookies
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche 
                    Erfahrung auf unserer Website zu bieten. Einige davon sind erforderlich, 
                    während andere uns helfen, diese Website und Ihre Erfahrung zu verbessern.{' '}
                    <Link 
                      to="/datenschutz" 
                      className="text-primary hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Mehr erfahren
                    </Link>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="w-full sm:w-auto"
              >
                Einstellungen
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRejectAll}
                className="w-full sm:w-auto"
              >
                Ablehnen
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleAcceptAll}
                className="w-full sm:w-auto"
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <CookieSettingsModal 
          onClose={() => {
            setShowSettings(false);
            // Wenn kein Consent vorhanden war, zeige Banner wieder
            const consent = localStorage.getItem('cookie-consent');
            if (!consent) {
              setShowBanner(true);
            }
          }}
          onSave={handleSaveSettings}
        />
      )}
    </>
  );
};

const CookieSettingsModal = ({ onClose, onSave }) => {
  // Lade aktuelle Einstellungen aus localStorage
  const getInitialSettings = () => {
    const consent = localStorage.getItem('cookie-consent');
    if (consent) {
      const parsed = JSON.parse(consent);
      return {
        necessary: true,
        analytics: parsed.analytics || false,
        marketing: parsed.marketing || false,
        preferences: parsed.preferences || false
      };
    }
    return {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
  };

  const [settings, setSettings] = useState(getInitialSettings());

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Notwendige Cookies',
      description: 'Diese Cookies sind für das Funktionieren der Website erforderlich.',
      required: true
    },
    {
      key: 'analytics',
      title: 'Analyse Cookies',
      description: 'Helfen uns zu verstehen, wie Besucher mit der Website interagieren.'
    },
    {
      key: 'marketing',
      title: 'Marketing Cookies',
      description: 'Werden verwendet, um Werbung relevanter zu gestalten.'
    },
    {
      key: 'preferences',
      title: 'Präferenz Cookies',
      description: 'Ermöglichen es der Website, sich an Ihre Präferenzen zu erinnern.'
    }
  ];

  const handleToggle = (key) => {
    if (key === 'necessary') return; // Kann nicht deaktiviert werden
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = () => {
    onSave(settings);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" 
      style={{ zIndex: 9999 }}
      onClick={(e) => {
        // Schließe Modal wenn auf Overlay geklickt wird
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" 
        style={{ zIndex: 10000 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-text-primary">Cookie Einstellungen</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            {cookieTypes.map((type) => (
              <div key={type.key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-text-primary">{type.title}</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[type.key]}
                      onChange={() => handleToggle(type.key)}
                      disabled={type.required}
                      className="sr-only peer"
                    />
                    <div className={`relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer 
                      ${settings[type.key] ? 'peer-checked:bg-primary' : ''} 
                      ${type.required ? 'opacity-50' : 'peer-checked:after:translate-x-full'} 
                      peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] 
                      after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all`}>
                    </div>
                  </label>
                </div>
                <p className="text-sm text-text-secondary">{type.description}</p>
                {type.required && (
                  <p className="text-xs text-primary mt-1">Immer aktiv</p>
                )}
              </div>
            ))}
          </div>

          <div className="mb-4 text-xs text-text-secondary">
            <Link 
              to="/datenschutz" 
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Weitere Informationen zur Datenverarbeitung finden Sie in unserer Datenschutzerklärung
            </Link>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Abbrechen
            </Button>
            <Button onClick={handleSave} className="flex-1">
              Einstellungen speichern
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;